import { useTranslation } from 'react-i18next';
import { ContactClassificationDtoIn } from '@/models/in/SupportedDtoIn';
import { DocumentWithDetailsDtoIn } from '@/models/in/DocumentWithDetailsDtoIn';
import { DocumentStatus } from '@/models/common/DocumentStatus';
import { getExpirationStatus, getExpirationStatusColor } from '@/helpers/contactClassificationExpiration';
import { CSSProperties } from 'react';
import DownloadFileButton from '@/components/ui/DownloadFileButton/DownloadFileButton';
import styles from './DocsTable.module.css';

interface DocsTableProps {
  contactClassification: ContactClassificationDtoIn;
}

const DocsTable = ({ contactClassification }: DocsTableProps) => {
  const { t } = useTranslation();

  const expiryText = contactClassification.expiresTimestamp ? `${t('valid')} ${new Date(contactClassification.expiresTimestamp).toLocaleDateString()}` : '';

  const expirationStatus = getExpirationStatus(contactClassification);
  const statusColor = getExpirationStatusColor(expirationStatus);

  const hasDocuments = contactClassification.documents && contactClassification.documents.length > 0;
  const hasDetails = hasDocuments && contactClassification.documents!.some((d) => d.status || d.officeComment || d.userComment);

  return (
    <div className={styles['table-container']}>
      <div className={styles['table-header']}>
        <div className="body-md-bold text-(--font-sub-title-2)">{t(contactClassification.classification.name)}</div>
        {expiryText && (
          <>
            <div className={styles['header-divider']} />
            <div className="body-md-semibold">{expiryText}</div>
          </>
        )}
        <div className={styles['header-divider']} />
        <div className={styles['classification-status']}>
          <span className={styles['classification-color']} style={{ '--classification-color': statusColor } as CSSProperties}></span>
          <span className="body-md-medium">{expirationStatus ? t(`ExpirationStatus.${expirationStatus}`) : ''}</span>
        </div>
      </div>

      {hasDocuments && (
        <table className={styles['table']}>
          <thead className={styles['table-thead']}>
            <tr>
              <th className="body-xs-sm-bold w-[180px]">
                {t('document')}/{t('approval')}
              </th>
              <th className="body-xs-sm-bold">{t('uploadDate')}</th>
              {hasDetails && (
                <>
                  <th className="body-xs-sm-bold">{t('status')}</th>
                  <th className="body-xs-sm-bold">{t('officeComment')}</th>
                  <th className="body-xs-sm-bold">{t('userComment')}</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className={styles['table-tbody']}>
            {contactClassification.documents?.map((doc: DocumentWithDetailsDtoIn, idx: number) => (
              <tr key={idx}>
                <td className={styles['table-cell-doc']}>
                  <div className="flex items-center gap-1">
                    <a href={doc.uri} target="_blank" rel="noreferrer" className="body-sm-link text-(--font-link)">
                      {doc.name}
                    </a>
                    <DownloadFileButton uri={doc.uri} fileName={doc.name} />
                  </div>
                </td>
                <td className={styles['table-cell-date']}>{new Date(doc.creationTime).toLocaleDateString()}</td>
                {hasDetails && (
                  <>
                    <td className={styles['table-cell-date']}>{doc.status === DocumentStatus.Rejected ? t('documentStatusRejected') : doc.status || '-'}</td>
                    <td className={styles['table-cell-date']}>{doc.officeComment || '-'}</td>
                    <td className={styles['table-cell-date']}>{doc.userComment || '-'}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default DocsTable;
